import { memo, useState, useCallback, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Mic2, X, Loader2, CheckCircle, AlertCircle, Play, Pause, Download, Eye } from 'lucide-react';
import { downloadFile } from '../../utils/downloadFile';
import { useWorkflowStore } from '../../store/workflowStore';
import { useNodeExecution } from '../../hooks/useNodeExecution';
import { useWorkflowTrigger } from '../../hooks/useWorkflowTrigger';
import NodeError from './NodeError';
import NodeResetButton from './NodeResetButton';
import {
  ReconfigureButton, ReconfigureNotice, RegenerateButton, useReconfigure,
} from './ReconfigureControls';
import { useMediaPreview } from '../../hooks/useMediaPreview';
import SendToTimelineButton from '../SendToTimelineButton';
import PromptField from '../studio/PromptField';
import NodeRequirements from './NodeRequirements';
import { missingOf, requirementsTitle, textNeed } from '../../utils/nodeRequirements';

const PRESET_PROMPTS = [
  { id: 'cinematic', label: 'Cinematic', emoji: '🎬', prompt: 'A cinematic orchestral piece with epic brass swells, thunderous timpani rolls and soaring string melodies' },
  { id: 'electronic', label: 'Electronic', emoji: '🎧', prompt: 'Modern electronic dance music with deep bass, crisp hi-hats, and euphoric synth melodies' },
  { id: 'jazz', label: 'Jazz', emoji: '🎷', prompt: 'Smooth jazz with warm saxophone melodies, soft piano chords, and gentle brushed drums' },
  { id: 'ambient', label: 'Ambient', emoji: '🌊', prompt: 'Ethereal ambient soundscape with soft pads, gentle drones, and peaceful atmospheric textures' },
];

const Lyria2Node = memo(({ id, data, selected }) => {
  const [chronometer, setChronometer] = useState(0);
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [negativePrompt, setNegativePrompt] = useState(data.negativePrompt || '');
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  
  const { processNode, status: execStatus, error: execError, reset: resetExec, insufficientCredits } = useNodeExecution();
  const isProcessing = execStatus === 'uploading' || execStatus === 'queued' || execStatus === 'processing';
  const { openAudioPreview } = useMediaPreview();

  // El prompt tiene que persistirse en node.data: si vive sólo en el estado
  // de React, el servidor no lo recibe (validate_workflow lo marca como
  // faltante) y se pierde al recargar el proyecto.
  useEffect(() => {
    updateNodeData(id, { prompt });
  }, [id, prompt, updateNodeData]);

  // Chronometer effect
  useEffect(() => {
    let interval;
    if (isProcessing) {
      setChronometer(0);
      interval = setInterval(() => {
        setChronometer((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isProcessing]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePresetClick = (preset) => {
    setSelectedPreset(preset.id);
    setPrompt(preset.prompt);
  };

  const togglePlay = useCallback(() => {
    const audio = document.getElementById(`lyria2-audio-${id}`);
    if (audio) {
      if (isPlaying) {
        audio.pause();
      } else {
        audio.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [id, isPlaying]);

  const handleProcess = useCallback(async () => {
    if (!prompt.trim()) return;
    resetExec();

    const result = await processNode({
      resultType: 'lyria2',
      params: {
        prompt: prompt.trim(),
        ...(negativePrompt.trim() ? { negative_prompt: negativePrompt.trim() } : {}),
      },
    });

    if (result?.outputUrl) {
      updateNodeData(id, {
        hasOutput: true,
        outputUrl: result.outputUrl,
        assetId: result.assetId ?? null,
        outputType: 'audio',
        processedAt: new Date().toISOString(),
      });
      addAsset({
        id: `asset_${Date.now()}`,
        type: 'audio',
        tool: 'lyria2',
        filename: `lyria2_${prompt.slice(0, 20)}`,
        url: result.outputUrl,
        createdAt: new Date().toISOString(),
      });
      return true;
    }
    return false;
  }, [id, prompt, negativePrompt, updateNodeData, addAsset, processNode, resetExec]);

  // Regenerar descarta el resultado; volver a configurar lo conserva.
  const { reconfigure, setReconfigure, showForm } = useReconfigure(data);
  const handleRegenerate = useCallback(() => {
    setReconfigure(false);
    updateNodeData(id, { hasOutput: false, outputUrl: null });
  }, [id, updateNodeData, setReconfigure]);
  const { isInQueue, isMyTurn } = useWorkflowTrigger(id, handleProcess);

  // NR-1 · qué le falta al nodo para generar (se muestra debajo del botón)
  const runMissing = missingOf([textNeed(prompt, 'describir la música que querés')]);

  return (
    <div className={`node-container lyria2-node ${selected ? 'selected' : ''} ${isInQueue ? 'workflow-queued' : ''} ${isMyTurn ? 'workflow-active' : ''}`} data-testid="lyria2-node">
      <NodeResetButton nodeId={id} hasOutput={!!data.hasOutput} />
      {reconfigure && (
        <ReconfigureNotice
          previewUrl={data.thumbnailUrl || null}
          onCancel={() => setReconfigure(false)}
          testId="lyria2-reconfigure-notice"
        />
      )}
      {/* Header */}
      <div className="node-header lyria2-header">
        <div className="node-header-left">
          <Mic2 size={16} />
          <span>Lyria 2</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content lyria2-content">
        {/* Processing State */}
        {isProcessing ? (
          <div className="node-processing">
            <div className="node-chronometer">
              {formatTime(chronometer)}
            </div>
            <div className="node-processing-info">
              <Loader2 size={16} className="animate-spin" />
              <span>Generando música...</span>
            </div>
            <p className="node-processing-hint">
              Audio 48kHz estéreo, 30 segundos
            </p>
          </div>
        ) : !showForm ? (
          <div className="node-result lyria2-result">
            <audio 
              id={`lyria2-audio-${id}`}
              src={data.outputUrl} 
              onEnded={() => setIsPlaying(false)}
            />
            <div className="lyria2-player">
              <button className="lyria2-play-btn" onClick={togglePlay}>
                {isPlaying ? <Pause size={24} /> : <Play size={24} />}
              </button>
              <div className="lyria2-waveform">
                {[...Array(12)].map((_, i) => (
                  <div 
                    key={`wave-${i}`} 
                    className={`lyria2-wave-bar ${isPlaying ? 'animate' : ''}`}
                    style={{ animationDelay: `${i * 0.1}s` }}
                  />
                ))}
              </div>
            </div>
            <div className="node-result-actions">
              <RegenerateButton onClick={handleRegenerate} testId="lyria2-regen-btn" />
              <ReconfigureButton onClick={() => setReconfigure(true)} testId="lyria2-reconfigure-btn" />
              <button 
                className="node-mini-preview-btn"
                onClick={() => openAudioPreview(data.outputUrl, 'Lyria 2 - Música')}
              >
                <Eye size={12} />
                Ver
              </button>
              <button 
                className="node-mini-download-btn"
                onClick={(e) => { e.stopPropagation(); downloadFile(data.outputUrl, `music_${Date.now()}.wav`); }}
              >
                <Download size={12} />
                Descargar WAV
              </button>
              <SendToTimelineButton assetId={data.assetId} nodeId={id} url={data.outputUrl} kind="audio" label="lyria2" testIdPrefix="lyria2-timeline" />
            </div>
            <div className="node-result-badge">
              <CheckCircle size={12} />
              <span>Música generada</span>
            </div>
          </div>
        ) : (
          <>
            {/* Preset buttons */}
            <div className="lyria2-presets">
              <label>Estilos rápidos</label>
              <div className="lyria2-preset-grid">
                {PRESET_PROMPTS.map((preset) => (
                  <button
                    key={preset.id}
                    className={`lyria2-preset-btn ${selectedPreset === preset.id ? 'active' : ''}`}
                    onClick={() => handlePresetClick(preset)}
                  >
                    <span className="preset-emoji">{preset.emoji}</span>
                    <span className="preset-label">{preset.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Prompt input */}
            <div className="lyria2-prompt-section">
              <label>Describe la música</label>
              <PromptField
                value={prompt}
                onChange={(text) => { setPrompt(text); setSelectedPreset(null); }}
                nodeId={id}
                nodeType="lyria2"
                label="Describe la música"
                placeholder="Ej: Música épica cinematográfica con orquesta..."
                className="lyria2-prompt-input"
                rows={3}
                testId="lyria2-prompt"
              />
            </div>

            {/* Negative prompt (optional) */}
            <div className="lyria2-negative-section">
              <label>Excluir (opcional)</label>
              <input
                type="text"
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                placeholder="Ej: voces, percusión fuerte"
                className="lyria2-negative-input"
              />
            </div>

            {/* Info */}
            <p className="lyria2-info">
              Genera 30s de música 48kHz estéreo
            </p>

            {/* Process Button */}
            <button
              className="node-process-btn lyria2-process-btn"
              onClick={handleProcess}
              disabled={!prompt.trim()}
              title={requirementsTitle(runMissing)}
              data-testid="lyria2-generate-btn"
            >
              <Mic2 size={14} />
              Generar Música
            </button>
            <NodeRequirements missing={runMissing} />
          </>
        )}

        {/* Error Display */}
        <NodeError error={execError} insufficientCredits={insufficientCredits} />
      </div>
      
      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

Lyria2Node.displayName = 'Lyria2Node';

export default Lyria2Node;
